import React from 'react';
import { InlineMath, BlockMath } from 'react-katex';
import FractionInput, { formatLatexPreview } from '../../../../components/inputs/FractionInput';

/* ──────────── HELPERY ──────────── */

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
const powi = (a, k) => (k >= 0 ? Math.pow(a, k) : 1 / Math.pow(a, -k));
const isIntegerOrProperFraction = (value) => Number.isInteger(value) || (Math.abs(value) > 0 && Math.abs(value) < 1);

const logb = (a) => (a === 10 ? '\\log' : `\\log_{${a}}`);
const logExp = (a) => (a === 10 ? '\\log_{10}' : `\\log_{${a}}`);

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    [a, b] = [b, a % b];
  }
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

function toFraction(x, maxDen = 1000) {
  const sign = x < 0 ? -1 : 1;
  x = Math.abs(x);
  if (!isFinite(x)) return { num: sign, den: 0 };

  let h0 = 0, k0 = 1;
  let h1 = 1, k1 = 0;
  let y = x;

  for (let i = 0; i < 18; i++) {
    const a = Math.floor(y);
    const h2 = a * h1 + h0;
    const k2 = a * k1 + k0;
    if (k2 > maxDen) break;
    h0 = h1;
    k0 = k1;
    h1 = h2;
    k1 = k2;
    const frac = y - a;
    if (frac < 1e-12) break;
    y = 1 / frac;
  }

  let num = h1;
  let den = k1;
  if (k0 !== 0 && Math.abs(h0 / k0 - x) < Math.abs(num / den - x)) {
    num = h0;
    den = k0;
  }

  ({ num, den } = simplifyFrac(num * sign, den));
  return { num, den };
}

function niceLatexFromNumber(val) {
  if (!isFinite(val)) return String(val);

  const int = Math.round(val);
  if (Math.abs(val - int) < 1e-12) return String(int);

  let { num, den } = toFraction(val);
  ({ num, den } = simplifyFrac(num, den));

  const sign = num < 0 ? '-' : '';
  num = Math.abs(num);

  return `${sign}\\tfrac{${num}}{${den}}`;
}

function powerLatex(baseLatex, r) {
  const needsParens = /\\dfrac|\\frac|\\tfrac/.test(baseLatex);
  const B = needsParens ? `\\left(${baseLatex}\\right)` : baseLatex;
  return `${B}^{${r}}`;
}

function coefLogLatex(a, r, argLatex) {
  if (r === 1) return `${logb(a)} ${argLatex}`;
  return `${r}\\,${logb(a)} ${argLatex}`;
}

const NICE_BY_BASE = {
  10: [2, 4, 5, 8, 20, 1 / 2, 1 / 4, 1 / 5],
   5: [2, 4, 8, 1 / 2, 1 / 4],
   4: [2, 3, 6, 1 / 2, 1 / 4],
   3: [2, 4, 6, 1 / 2, 1 / 3],
   2: [3, 5, 6, 1 / 2, 3 / 4],
};

function isPurePowerOfBase(val, a) {
  const p = Math.log(val) / Math.log(a);
  return Math.abs(p - Math.round(p)) < 1e-10;
}

/* ──────────── GENERATOR ──────────── */

export function generateTask() {
  const a = pick([2, 3, 4, 5, 10]);
  const pool = NICE_BY_BASE[a] || NICE_BY_BASE[10];
  const exps = [-2, -1, 0, 1, 2];

  let X, Y, Xs, Ys, r1, r2, op, t;

  for (let tries = 0; tries < 200; tries++) {
    // współczynniki — co najmniej jedna „2”
    do {
      r1 = pick([1, 2, 3]);
      r2 = pick([1, 2, 3]);
    } while (r1 !== 2 && r2 !== 2);

    op = pick(['+', '-']);

    const m1 = pick(pool);
    const m2 = pick(pool);
    const u1 = pick(exps);
    const u2 = pick(exps);

    X = m1 * powi(a, u1);
    Y = m2 * powi(a, u2);

    if (X <= 0 || Y <= 0 || !isFinite(X) || !isFinite(Y)) continue;
    if (!isIntegerOrProperFraction(X) || !isIntegerOrProperFraction(Y)) continue;

    // unikamy X = Y (żeby nie było trywialnych przykładów)
    if (Math.abs(X - Y) < 1e-12) continue;

    // unikamy czystych potęg a
    if (isPurePowerOfBase(X, a) || isPurePowerOfBase(Y, a)) continue;

    // po włożeniu współczynników
    const F = op === '+'
      ? Math.pow(X, r1) * Math.pow(Y, r2)
      : Math.pow(X, r1) / Math.pow(Y, r2);

    if (!isFinite(F) || F <= 0) continue;

    // chcemy F = a^t z ładnym t w przedziale [-3,3], z prostym mianownikiem
    const pFloat = Math.log(F) / Math.log(a);
    const { num, den } = toFraction(pFloat, 4);
    if (!den) continue;

    const tCandidate = num / den;
    if (Math.abs(pFloat - tCandidate) > 1e-8) continue;
    if (Math.abs(tCandidate) > 3 || tCandidate === 0) continue;

    t = tCandidate;
    Xs = niceLatexFromNumber(X);
    Ys = niceLatexFromNumber(Y);
    break;
  }

  // fallback na prostszy przypadek z czystymi potęgami
  if (t == null) {
    const ks = [-3, -2, -1, 1, 2, 3];
    while (true) {
      const k1 = pick(ks);
      let k2;
      do { k2 = pick(ks); } while (k2 === k1);

      do {
        r1 = pick([1, 2, 3]);
        r2 = pick([1, 2, 3]);
      } while (r1 !== 2 && r2 !== 2);

      op = pick(['+', '-']);

      const part1 = r1 * k1;
      const part2 = r2 * k2;
      const tInt = op === '+' ? part1 + part2 : part1 - part2;

      if (tInt !== 0 && Math.abs(tInt) <= 3) {
        t = tInt;
        X = powi(a, k1);
        Y = powi(a, k2);
        Xs = niceLatexFromNumber(X);
        Ys = niceLatexFromNumber(Y);
        break;
      }
    }
  }

  return {
    type: 'LogPropCoefMixTask',
    data: { a, r1, r2, op, X, Y, Xs, Ys, t }
  };
}

/* ──────────── RENDER TASK ──────────── */

export function renderTask({ data }) {
  const { a, Xs, Ys, r1, r2, op } = data;
  const expr = `${coefLogLatex(a, r1, Xs)} ${op} ${coefLogLatex(a, r2, Ys)}`;

  return (
    <div>
      Oblicz wartość wyrażenia:
      <div style={{ marginTop: 14, marginBottom: 14 }}>
        <BlockMath math={expr} />
      </div>
    </div>
  );
}

/* ──────────── INPUT ──────────── */

export function renderInput(task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
    />
  );
}

/* ──────────── CHECKING ──────────── */

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== 'object') return false;
  const v = userAnswer.parsed;
  return v != null && Math.abs(v - data.t) < 1e-4;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw) return <i>(brak odpowiedzi)</i>;
  return <InlineMath math={formatLatexPreview(userAnswer.raw)} />;
}

export function renderAnswer({ data }) {
  return <InlineMath math={niceLatexFromNumber(data.t)} />;
}

/* ──────────── EXPLANATION ──────────── */

export function renderExplanation({ data }) {
  const { a, Xs, Ys, r1, r2, op, t } = data;

  const Xpow = powerLatex(Xs, r1);
  const Ypow = powerLatex(Ys, r2);
  const combined =
    op === '+'
      ? `${Xpow}\\cdot${Ypow}`
      : `\\tfrac{${Xpow}}{${Ypow}}`;

  const numericVal = Math.pow(a, t);
  const numericLatex = niceLatexFromNumber(numericVal);

  const baseLatex = a === 10 ? '10' : String(a);
  const tLatex = niceLatexFromNumber(t);

  return (
    <div className="space-y-2">

      <p><strong>Krok 1:</strong> Własność współczynnika przy logarytmie.</p>
      <BlockMath math={'r\\,\\log_{a} x = \\log_{a}(x^{r})'} />
      {a === 10 && (
        <p style={{ marginTop: 6 }}>
          <em>Uwaga:</em> zapis <InlineMath math={"\\log"} /> oznacza logarytm dziesiętny, czyli{' '}
          <span style={{ whiteSpace: 'nowrap' }}>
            <InlineMath math={"\\log x = \\log_{10} x"} />
          </span>.
        </p>
      )}

      <p><strong>Krok 2:</strong> Zastosuj tę własność do obu logarytmów.</p>
      <BlockMath
        math={
          `${coefLogLatex(a, r1, Xs)} ${op} ${coefLogLatex(a, r2, Ys)} = `
          + `${logExp(a)} ${Xpow} ${op} ${logExp(a)} ${Ypow}`
        }
      />

      <p><strong>Krok 3:</strong> Skorzystaj z własności sumy/różnicy logarytmów.</p>
      <BlockMath
        math={
          `${logExp(a)} ${Xpow} ${op} ${logExp(a)} ${Ypow}`
          + ` = ${logExp(a)}\\left(${combined}\\right)`
        }
      />

      <p><strong>Krok 4:</strong> Uprość argument logarytmu liczbowo.</p>
      <BlockMath math={`${combined} = ${numericLatex}`} />

      <p><strong>Krok 5:</strong> Zamień tę liczbę na potęgę podstawy i odczytaj wartość logarytmu.</p>
      <BlockMath math={`${numericLatex} = ${baseLatex}^{${tLatex}}`} />
      <BlockMath math={`${logExp(a)} ${baseLatex}^{${tLatex}} = ${tLatex}`} />

      <p><strong>Wniosek:</strong></p>
      <BlockMath
        math={`${coefLogLatex(a, r1, Xs)} ${op} ${coefLogLatex(a, r2, Ys)} = ${tLatex}`}
      />
    </div>
  );
}
